import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { listExtensionSkills, listExtensionTools } from '../../api/extensions.api';
import {
  EXAMPLE_SKILLS,
  EXAMPLE_TOOLS,
  TOOL_SOURCE_LABELS,
} from '../../pages/studio/studioExtensionExamples';

function groupTools(tools) {
  const groups = {};
  tools.forEach((tool) => {
    const key = tool.source || 'other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(tool);
  });
  return groups;
}

function ToolList({ tools, emptyLabel }) {
  const groups = useMemo(() => groupTools(tools), [tools]);

  if (!tools.length) {
    return (
      <Typography variant="body2" color="text.disabled">{emptyLabel}</Typography>
    );
  }

  const order = ['builtin', 'extension', 'connector-op', 'flow-node', 'flow', 'other'];

  return (
    <Stack spacing={2}>
      {order.filter((k) => groups[k]?.length).map((source) => (
        <Box key={source}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.06 }}>
            {TOOL_SOURCE_LABELS[source] || source}
            {' '}
            ({groups[source].length})
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {groups[source].map((tool) => (
              <Box
                key={tool.id || tool.name}
                sx={{
                  p: 1, border: 1, borderColor: 'divider', borderRadius: 1.5,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                    {tool.name}
                  </Typography>
                  {tool.extensionId && <Chip size="small" label={tool.extensionId} />}
                </Stack>
                {tool.description && (
                  <Typography variant="caption" color="text.secondary">{tool.description}</Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function SkillList({ skills }) {
  if (!skills.length) {
    return (
      <Typography variant="body2" color="text.disabled">No skills available for enabled extensions.</Typography>
    );
  }

  return (
    <Stack spacing={0.75}>
      {skills.map((skill) => (
        <Box
          key={skill.id}
          sx={{
            p: 1, border: 1, borderColor: 'divider', borderRadius: 1.5,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={700}>{skill.name || skill.id}</Typography>
            {skill.extensionId && <Chip size="small" label={skill.extensionId} />}
          </Stack>
          {skill.description && (
            <Typography variant="caption" color="text.secondary">{skill.description}</Typography>
          )}
          {skill.allowedTools?.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {skill.allowedTools.map((t) => (
                <Chip key={t} size="small" variant="outlined" label={t} />
              ))}
            </Stack>
          )}
        </Box>
      ))}
    </Stack>
  );
}

export default function StudioV2ToolCatalog({ embedded = false }) {
  const [tools, setTools] = useState([]);
  const [skills, setSkills] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('live');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [toolsRes, skillsRes] = await Promise.all([
        listExtensionTools(),
        listExtensionSkills(),
      ]);
      if (cancelled) return;
      setTools(toolsRes.tools || []);
      setSkills(skillsRes.skills || []);
      setError(toolsRes.error || skillsRes.error || '');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading tools…</Typography>
      </Box>
    );
  }

  const showExamplesFallback = tools.length === 0;

  return (
    <Stack spacing={1.5}>
      {!embedded && (
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Tools & skills catalog</Typography>
          <Typography variant="body2" color="text.secondary">
            Live registry for your tenant (enabled extensions + your saved flows).
          </Typography>
        </Box>
      )}
      {embedded && (
        <Typography variant="body2" color="text.secondary">
          Tools and skills from enabled plugins. Use these names on mcp-tool and AI steps.
        </Typography>
      )}
      {error && (
        <Alert severity="warning">
          {error}
          {' '}
          Showing bundled examples until the catalog loads.
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
        <Tab value="live" label={`Live (${tools.length} tools · ${skills.length} skills)`} />
        <Tab value="examples" label="Examples" />
      </Tabs>
      {tab === 'live' && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Tools</Typography>
            {showExamplesFallback ? (
              <>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  No live tools yet. Enable plugins and save, or open Examples.
                </Alert>
                <ToolList tools={EXAMPLE_TOOLS.filter((t) => t.id !== 'flow.example')} emptyLabel="No tools registered." />
              </>
            ) : (
              <ToolList tools={tools} emptyLabel="No tools registered." />
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Skills</Typography>
            {skills.length === 0 ? (
              <SkillList skills={EXAMPLE_SKILLS} />
            ) : (
              <SkillList skills={skills} />
            )}
          </Box>
        </Stack>
      )}
      {tab === 'examples' && (
        <Stack spacing={2}>
          <ToolList tools={EXAMPLE_TOOLS} emptyLabel="" />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Skills</Typography>
            <SkillList skills={EXAMPLE_SKILLS} />
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
